/**
 * StorageService - Browser Storage Service with Caching
 *
 * Provides centralized storage access with in-memory caching
 * to reduce excessive storage access and improve performance.
 *
 * FEATURES:
 * - In-memory caching to minimize storage reads
 * - Support for both localStorage and sessionStorage
 * - Subscription system for reactive updates
 * - Access counting for performance monitoring
 * - JSON helpers for object serialization
 * - TTL (Time-To-Live) support for temporary data
 * - Namespace support for scoped operations
 * - Resilience to storage access blocks (Tracking Prevention)
 *
 * @module @puffin-core/storage/StorageService
 */

import { assertNamespaceName } from '@puffin-core/storage-namespace-name';

/**
 * Storage type constants
 * @readonly
 * @enum {string}
 */
export const STORAGE_TYPE = Object.freeze({
  LOCAL: 'local',
  SESSION: 'session',
});

/** @type {StorageService|null} Singleton instance */
let _instance = null;

/**
 * The browser's Web Storage object, or `null` when there is none — no DOM, or a browser that
 * REFUSES it: the `window.localStorage` / `sessionStorage` getter itself throws a `SecurityError`
 * DOMException (HTML Living Standard, "Web storage"; blocked cookies, some privacy modes and
 * sandboxed frames). That refusal is the one expected failure, and the service then runs on its
 * in-memory cache; anything else thrown by the getter is a defect and surfaces.
 *
 * Every consumer that relies on this service absorbing a refused store (auth-token-storage, the
 * list and i18n persistence) depends on this guard: without it the first `getInstance()` threw
 * (SWALLOWED-CATCH-BURNDOWN-001 U3).
 *
 * @param {'localStorage'|'sessionStorage'} name
 * @returns {Storage|null}
 */
function resolveWebStorage(name) {
  try {
    return typeof globalThis[name] !== 'undefined' ? globalThis[name] : null;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'SecurityError') return null;
    throw error;
  }
}

/**
 * Storage Service with caching and resilience
 */
export class StorageService {
  /**
   * Gets the singleton instance
   * @returns {StorageService} The storage service instance
   */
  static getInstance() {
    if (!_instance) {
      _instance = new StorageService();
    }
    return _instance;
  }

  /**
   * Resets the singleton (creates new instance on next getInstance call)
   * Primarily for testing
   */
  static resetInstance() {
    if (_instance) {
      _instance.reset();
    }
    _instance = null;
  }

  /**
   * Alias for resetInstance()
   * Note: Prefer using resetInstance() for clarity
   */
  static reset() {
    StorageService.resetInstance();
  }
  /**
   * Create a StorageService instance
   *
   * @param {Object} [options] - Configuration options
   * @param {Object} [options.logger] - Logger instance
   * @param {Storage} [options.localStorage] - localStorage (for testing)
   * @param {Storage} [options.sessionStorage] - sessionStorage (for testing)
   */
  constructor(options = {}) {
    this.logger = options.logger || null;
    this._localStorage = options.localStorage || resolveWebStorage('localStorage');
    this._sessionStorage = options.sessionStorage || resolveWebStorage('sessionStorage');

    /** @type {Map<string, string|null>} In-memory cache for localStorage */
    this.localCache = new Map();

    /** @type {Map<string, string|null>} In-memory cache for sessionStorage */
    this.sessionCache = new Map();

    /** @type {Map<string, Set<Function>>} Subscribers for localStorage keys */
    this.localSubscribers = new Map();

    /** @type {Map<string, Set<Function>>} Subscribers for sessionStorage keys */
    this.sessionSubscribers = new Map();

    /** @type {boolean} Whether initialize has been called */
    this.initialized = false;

    /** @type {number} Count of storage accesses for monitoring */
    this.accessCount = 0;
  }

  /**
   * Gets the appropriate storage object
   * @private
   * @param {string} type - Storage type ('local' or 'session')
   * @returns {Storage|null} The storage object
   */
  _getStorage(type) {
    return type === STORAGE_TYPE.SESSION ? this._sessionStorage : this._localStorage;
  }

  /**
   * Gets the appropriate cache
   * @private
   * @param {string} type - Storage type ('local' or 'session')
   * @returns {Map<string, string|null>} The cache map
   */
  _getCache(type) {
    return type === STORAGE_TYPE.SESSION ? this.sessionCache : this.localCache;
  }

  /**
   * Gets the appropriate subscribers map
   * @private
   * @param {string} type - Storage type ('local' or 'session')
   * @returns {Map<string, Set<Function>>} The subscribers map
   */
  _getSubscribers(type) {
    return type === STORAGE_TYPE.SESSION ? this.sessionSubscribers : this.localSubscribers;
  }

  /**
   * Notifies subscribers of a value change
   * @private
   * @param {string} key - Storage key
   * @param {string|null} newValue - New value
   * @param {string|null} oldValue - Previous value
   * @param {string} type - Storage type
   */
  _notifySubscribers(key, newValue, oldValue, type) {
    const subscribers = this._getSubscribers(type);
    const keySubscribers = subscribers.get(key);

    if (keySubscribers) {
      keySubscribers.forEach((callback) => {
        try {
          callback(newValue, oldValue);
        } catch (error) {
          this._log('error', 'Subscriber error', { key, type, error: error.message });
        }
      });
    }
  }

  /**
   * Log a message
   * @private
   * @param {string} level - Log level
   * @param {string} message - Message
   * @param {Object} [data] - Additional data
   */
  _log(level, message, data) {
    if (this.logger && typeof this.logger[level] === 'function') {
      this.logger[level](`StorageService: ${message}`, data);
    }
  }

  /**
   * Safely reads from storage, returning null if access is blocked
   * @private
   * @param {Storage|null} storage - Storage object
   * @param {string} key - Storage key
   * @returns {string|null} The value or null if blocked/error
   */
  _safeGetItem(storage, key) {
    if (!storage) {
      return null;
    }

    try {
      return storage.getItem(key);
    } catch (error) {
      this._log('debug', 'Storage access blocked for get', { key, error: error.name });
      return null;
    }
  }

  /**
   * Safely writes to storage, silently failing if access is blocked
   * @private
   * @param {Storage|null} storage - Storage object
   * @param {string} key - Storage key
   * @param {string} value - Value to store
   */
  _safeSetItem(storage, key, value) {
    if (!storage) {
      return;
    }

    try {
      storage.setItem(key, value);
    } catch (error) {
      this._log('debug', 'Storage access blocked for set', { key, error: error.name });
    }
  }

  /**
   * Safely removes from storage, silently failing if access is blocked
   * @private
   * @param {Storage|null} storage - Storage object
   * @param {string} key - Storage key
   */
  _safeRemoveItem(storage, key) {
    if (!storage) {
      return;
    }

    try {
      storage.removeItem(key);
    } catch (error) {
      this._log('debug', 'Storage access blocked for remove', { key, error: error.name });
    }
  }

  /**
   * Initializes the service by pre-loading specified keys into cache
   * Only runs once - subsequent calls are ignored
   *
   * @param {string[]} localKeys - localStorage keys to pre-load
   * @param {string[]} [sessionKeys=[]] - sessionStorage keys to pre-load
   */
  initialize(localKeys, sessionKeys = []) {
    if (this.initialized) {
      return;
    }

    // Pre-load localStorage keys
    localKeys.forEach((key) => {
      const value = this._safeGetItem(this._localStorage, key);
      this.localCache.set(key, value);
      this.accessCount++;
    });

    // Pre-load sessionStorage keys
    sessionKeys.forEach((key) => {
      const value = this._safeGetItem(this._sessionStorage, key);
      this.sessionCache.set(key, value);
      this.accessCount++;
    });

    this.initialized = true;
  }

  /**
   * Gets a value from cache (or storage if not cached)
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {string|null} The stored value or null
   */
  get(key, type = STORAGE_TYPE.LOCAL) {
    const cache = this._getCache(type);

    // Return cached value if available
    if (cache.has(key)) {
      return cache.get(key);
    }

    // Key not in cache - read from storage and cache it
    const storage = this._getStorage(type);
    const value = this._safeGetItem(storage, key);
    cache.set(key, value);
    this.accessCount++;

    return value;
  }

  /**
   * Sets a value in cache and storage
   *
   * @param {string} key - Storage key
   * @param {string|null} value - Value to store (null removes the key)
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  set(key, value, type = STORAGE_TYPE.LOCAL) {
    const cache = this._getCache(type);
    const storage = this._getStorage(type);

    const oldValue = cache.get(key) || null;

    if (value === null) {
      // Remove key (cache first, then attempt storage)
      cache.set(key, null);
      this._safeRemoveItem(storage, key);
    } else {
      // Set key (cache first, then attempt storage)
      cache.set(key, value);
      this._safeSetItem(storage, key, value);
    }

    this.accessCount++;
    this._notifySubscribers(key, value, oldValue, type);
  }

  /**
   * Removes a key from cache and storage
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  remove(key, type = STORAGE_TYPE.LOCAL) {
    const cache = this._getCache(type);
    const storage = this._getStorage(type);

    const oldValue = cache.get(key) || null;

    cache.set(key, null);
    this._safeRemoveItem(storage, key);

    this.accessCount++;
    this._notifySubscribers(key, null, oldValue, type);
  }

  /**
   * Invalidates cache for a key, forcing next get to read from storage
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  invalidate(key, type = STORAGE_TYPE.LOCAL) {
    const cache = this._getCache(type);
    cache.delete(key);
  }

  /**
   * Gets all keys from storage
   *
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {string[]} Array of storage keys
   */
  keys(type = STORAGE_TYPE.LOCAL) {
    const storage = this._getStorage(type);

    if (!storage) {
      return [];
    }

    try {
      return Object.keys(storage);
    } catch (error) {
      // Narrowed (SWALLOWED-CATCH U3): only the browser's refusal of a blocked store is expected —
      // there are then no keys this tab can read. Anything else is a defect and surfaces.
      if (!(error instanceof DOMException)) throw error;
      return [];
    }
  }

  /**
   * Gets multiple values at once
   *
   * @param {string[]} keys - Array of storage keys
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {Object} Object with key-value pairs
   */
  getMultiple(keys, type = STORAGE_TYPE.LOCAL) {
    const result = {};

    keys.forEach((key) => {
      result[key] = this.get(key, type);
    });

    return result;
  }

  /**
   * Sets multiple values at once
   *
   * @param {Object} keyValuePairs - Object with key-value pairs
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  setMultiple(keyValuePairs, type = STORAGE_TYPE.LOCAL) {
    Object.entries(keyValuePairs).forEach(([key, value]) => {
      this.set(key, value, type);
    });
  }

  /**
   * Subscribes to changes for a specific key
   *
   * @param {string} key - Storage key to watch
   * @param {Function} callback - Function called on change: (newValue, oldValue) => void
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {Function} Unsubscribe function
   */
  subscribe(key, callback, type = STORAGE_TYPE.LOCAL) {
    const subscribers = this._getSubscribers(type);

    if (!subscribers.has(key)) {
      subscribers.set(key, new Set());
    }

    subscribers.get(key).add(callback);

    // Return unsubscribe function
    return () => {
      const keySubscribers = subscribers.get(key);
      if (keySubscribers) {
        keySubscribers.delete(callback);
      }
    };
  }

  /**
   * Returns a namespaced accessor for scoped storage operations
   *
   * @param {string} prefix - Namespace prefix (e.g., 'auth', 'analytics'); it may not contain `_`, the character that
   *   joins it to its keys (`@puffin-core/storage-namespace-name`)
   * @returns {Object} Scoped accessor with get, set, remove, subscribe methods
   * @throws {TypeError} For a prefix that is not a non-empty string, or contains `_`
   */
  getNamespace(prefix) {
    assertNamespaceName('StorageService', prefix);
    return {
      /**
       * Get value from namespaced key
       * @param {string} key - Key name (without prefix)
       * @param {string} [type='local'] - Storage type
       * @returns {string|null} The stored value
       */
      get: (key, type = STORAGE_TYPE.LOCAL) => this.get(`${prefix}_${key}`, type),

      /**
       * Set value for namespaced key
       * @param {string} key - Key name (without prefix)
       * @param {string|null} value - Value to store
       * @param {string} [type='local'] - Storage type
       */
      set: (key, value, type = STORAGE_TYPE.LOCAL) => this.set(`${prefix}_${key}`, value, type),

      /**
       * Remove namespaced key
       * @param {string} key - Key name (without prefix)
       * @param {string} [type='local'] - Storage type
       */
      remove: (key, type = STORAGE_TYPE.LOCAL) => this.remove(`${prefix}_${key}`, type),

      /**
       * Drop the cached copy of a namespaced key: the next read is what the browser holds now. Another tab writes the
       * same storage and this service's cache never hears it (PALM-OFFLINE-PUNCH-SYNC-001 G8: a queue two tabs share).
       * @param {string} key - Key name (without prefix)
       * @param {string} [type='local'] - Storage type
       */
      invalidate: (key, type = STORAGE_TYPE.LOCAL) => this.invalidate(`${prefix}_${key}`, type),

      /**
       * Subscribe to namespaced key changes
       * @param {string} key - Key name (without prefix)
       * @param {Function} callback - Change handler
       * @param {string} [type='local'] - Storage type
       * @returns {Function} Unsubscribe function
       */
      subscribe: (key, callback, type = STORAGE_TYPE.LOCAL) =>
        this.subscribe(`${prefix}_${key}`, callback, type),

      /**
       * Get JSON from namespaced key
       * @param {string} key - Key name (without prefix)
       * @param {string} [type='local'] - Storage type
       * @returns {*} Parsed value
       */
      getJSON: (key, type = STORAGE_TYPE.LOCAL) => this.getJSON(`${prefix}_${key}`, type),

      /**
       * Set JSON for namespaced key
       * @param {string} key - Key name (without prefix)
       * @param {*} value - Value to store
       * @param {string} [type='local'] - Storage type
       */
      setJSON: (key, value, type = STORAGE_TYPE.LOCAL) =>
        this.setJSON(`${prefix}_${key}`, value, type),

      /**
       * Set JSON for namespaced key, throwing when the browser refuses the write
       * @param {string} key - Key name (without prefix)
       * @param {*} value - Value to store
       * @param {string} [type='local'] - Storage type
       * @throws {Error} When the value could not be written to the browser's storage
       */
      getJSONDurably: (key, type = STORAGE_TYPE.LOCAL) =>
        this.getJSONDurably(`${prefix}_${key}`, type),
      setJSONDurably: (key, value, type = STORAGE_TYPE.LOCAL) =>
        this.setJSONDurably(`${prefix}_${key}`, value, type),
    };
  }

  /**
   * Gets a JSON-parsed value from storage
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {*} Parsed JSON value or null if invalid/not found
   */
  getJSON(key, type = STORAGE_TYPE.LOCAL) {
    const value = this.get(key, type);

    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      // Narrowed (SWALLOWED-CATCH U3): a stored value that is not JSON is expected (another
      // writer, an older build) and reads as no value. Anything else is a defect and surfaces.
      if (!(error instanceof SyntaxError)) throw error;
      return null;
    }
  }

  /**
   * Sets a JSON-stringified value in storage
   *
   * @param {string} key - Storage key
   * @param {*} value - Value to stringify and store
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  setJSON(key, value, type = STORAGE_TYPE.LOCAL) {
    const stringValue = JSON.stringify(value);
    this.set(key, stringValue, type);
  }

  /**
   * Reads a JSON value DURABLY: `null` ONLY when the key is absent. A browser that has no such storage, a blocked read,
   * and a stored value that is not JSON THROW — where `getJSON` answers all three with the same `null` as "nothing
   * stored". The read half of `setJSONDurably`, for a value whose loss is unrecoverable (UAT-BLOCKERS Q-20: an offline
   * punch queue read as empty is rewritten as `[newPunch]`, deleting every kept punch). It reads the storage, not the
   * in-memory copy, and keeps the copy current.
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {*} The parsed value, or null when nothing is stored under `key`
   * @throws {Error} When the browser has no such storage, or refused the read
   * @throws {SyntaxError} A stored value that is not JSON
   */
  getJSONDurably(key, type = STORAGE_TYPE.LOCAL) {
    const storage = this._getStorage(type);
    if (!storage) {
      throw new Error(`StorageService: no ${type} storage to read "${key}" from`);
    }
    const value = storage.getItem(key);
    if (value === null) return null;
    const parsed = JSON.parse(value);
    this._getCache(type).set(key, value);
    this.accessCount++;
    return parsed;
  }

  /**
   * Sets a JSON-stringified value, and THROWS when the browser refuses the write.
   *
   * `setJSON` keeps a refused value in memory and goes on — right for preferences, where the page should keep
   * working. A value that must outlive the page (PALM-OFFLINE-PUNCH-SYNC-001: a punch kept while offline) cannot be
   * reported as kept when it was not: storage that is full, blocked or missing throws here, and the in-memory copy is
   * left as it was, so the next read answers what the browser really holds.
   *
   * @param {string} key - Storage key
   * @param {*} value - Value to stringify and store
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @throws {Error} When the browser has no such storage, or refused the write
   */
  setJSONDurably(key, value, type = STORAGE_TYPE.LOCAL) {
    const storage = this._getStorage(type);
    if (!storage) {
      throw new Error(`StorageService: no ${type} storage to keep "${key}" in`);
    }
    const stringValue = JSON.stringify(value);
    storage.setItem(key, stringValue);
    const cache = this._getCache(type);
    const oldValue = cache.get(key) || null;
    cache.set(key, stringValue);
    this.accessCount++;
    this._notifySubscribers(key, stringValue, oldValue, type);
  }

  /**
   * Sets a JSON value with TTL (Time-To-Live)
   *
   * Data is stored with expiration metadata:
   * { __ttl: true, value: actualValue, expiresAt: timestamp }
   *
   * @param {string} key - Storage key
   * @param {*} value - Value to store
   * @param {number} ttlMs - Time to live in milliseconds
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  setJSONWithTTL(key, value, ttlMs, type = STORAGE_TYPE.LOCAL) {
    const wrapper = {
      __ttl: true,
      value: value,
      expiresAt: Date.now() + ttlMs,
    };
    this.setJSON(key, wrapper, type);
  }

  /**
   * Gets a JSON value with TTL checking
   *
   * Returns null if:
   * - Key doesn't exist
   * - Data is expired (and auto-removes it)
   * - Data is not TTL-wrapped (for safety)
   *
   * @param {string} key - Storage key
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {*} Value if not expired, null otherwise
   */
  getJSONWithTTL(key, type = STORAGE_TYPE.LOCAL) {
    const wrapper = this.getJSON(key, type);

    if (!wrapper) {
      return null;
    }

    // eslint-disable-next-line web/no-field-mapping -- __ttl is an internal marker property, not a database field
    if (!wrapper.__ttl) {
      return null;
    }

    if (Date.now() > wrapper.expiresAt) {
      this.remove(key, type);
      return null;
    }

    return wrapper.value;
  }

  /**
   * Gets the total number of storage accesses
   *
   * @returns {number} Total storage access count
   */
  getAccessCount() {
    return this.accessCount;
  }

  /**
   * Resets the access counter
   */
  resetAccessCount() {
    this.accessCount = 0;
  }

  /**
   * Resets the entire service state
   */
  reset() {
    this.localCache.clear();
    this.sessionCache.clear();
    this.localSubscribers.clear();
    this.sessionSubscribers.clear();
    this.initialized = false;
    this.accessCount = 0;
  }

  /**
   * Detects private/incognito/InPrivate browsing mode using multi-signal verification.
   *
   * The core question is: "Will localStorage persist across browser restarts?" rather
   * than "is this private browsing?". Private browsing clears localStorage on window
   * close, making device registration non-functional.
   *
   * Detection uses four independent signals evaluated in order of reliability:
   *
   * **Signal 1: localStorage write/read test (PRIMARY)**
   * - Writes a canary value, reads it back, cleans up
   * - If localStorage is unavailable or throws, storage is blocked → treat as private
   * - This is the most important signal: if we can't write, nothing else matters
   *
   * **Signal 2: navigator.storage.persisted() (POSITIVE-ONLY CONFIRMER)**
   * - `true` = definitively normal mode (browser confirmed durable, disk-backed storage)
   * - `false` = inconclusive (could be normal or private — unengaged origins return false)
   * - Uses persisted() (reads current state) NOT persist() (requests permission) to avoid
   *   triggering Firefox's storage permission prompt during silent detection
   * - Per the Storage Living Standard, browsers MUST NOT grant persistent storage in
   *   private sessions because the partition is ephemeral
   *
   * **Signal 3: File System Access API / OPFS (Chrome/Edge specific)**
   * - `navigator.storage.getDirectory()` succeeds in normal mode
   * - Throws `SecurityError` in Chrome/Edge private mode
   * - Not available in all browsers — supplementary signal only
   *
   * **Signal 4: Storage quota as SUPPLEMENTARY signal**
   * - Private browsing RAM-backed storage typically yields quota < 500 MB
   * - Only flags private when quota is very small (< 500 MB) to avoid false positives
   *   on development machines, VMs, or containers with small disks
   * - Never used as the sole signal — always supplementary
   *
   * **Fail-open design**: When all signals are inconclusive but localStorage works,
   * we return `{ isPrivate: false, confidence: 'low' }`. The localStorage write test
   * already confirmed storage is functional for this session.
   *
   * @returns {Promise<{ isPrivate: boolean, confidence: 'high' | 'low' }>}
   */
  static async detectPrivateBrowsing() {
    // No catch around the signals (SWALLOWED-CATCH U3). It used to answer ANY failure with
    // `{ isPrivate: false, confidence: 'unknown' }`. Signal 1 absorbs the browser's refusal itself;
    // once it has passed, the origin is a readable, non-opaque one, where `persisted()` and
    // `estimate()` resolve (they reject only for an opaque origin or a detached document), and
    // Signal 3 already narrows its own refusal. A rejection that reaches the caller is a defect.
    // --- Signal 1: localStorage write/read test (PRIMARY) ---
    // This is the most critical check. If we cannot write to localStorage,
    // device tokens cannot be stored regardless of browsing mode.
    if (!StorageService._testLocalStorageWrite()) {
      return { isPrivate: true, confidence: 'high' };
    }

    // --- Signal 2: navigator.storage.persisted() (POSITIVE-ONLY CONFIRMER) ---
    // IMPORTANT: Use persisted() (checks current state) NOT persist() (requests permission).
    // persist() triggers a user-facing permission prompt in Firefox, which is unacceptable
    // for a silent detection check. persisted() returns true if storage was previously
    // granted persistent status — no prompt, no side effects.
    if (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'persisted' in navigator.storage
    ) {
      const isPersisted = await navigator.storage.persisted();
      if (isPersisted) {
        return { isPrivate: false, confidence: 'high' };
      }
    }

    // --- Signal 3: File System Access API / OPFS (Chrome/Edge) ---
    // navigator.storage.getDirectory() succeeds in normal mode but throws
    // SecurityError in Chrome/Edge private mode. Not all browsers support
    // this API, so non-SecurityError failures are inconclusive.
    if (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'getDirectory' in navigator.storage
    ) {
      try {
        await navigator.storage.getDirectory();
        return { isPrivate: false, confidence: 'high' };
      } catch (err) {
        if (err && err.name === 'SecurityError') {
          return { isPrivate: true, confidence: 'high' };
        }
        // Other errors (NotSupportedError, etc.) — inconclusive, continue
      }
    }

    // --- Signal 4: Storage quota as SUPPLEMENTARY signal ---
    // Private browsing RAM-backed storage typically has quota < 500 MB.
    // We use a conservative 500 MB threshold to avoid false positives on
    // dev machines with small disks. This is never the sole signal — localStorage
    // already passed the write test above.
    if (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'estimate' in navigator.storage
    ) {
      const { quota } = await navigator.storage.estimate();
      if (typeof quota === 'number') {
        const VERY_LOW_QUOTA = 500 * 1024 * 1024; // 500 MB
        if (quota < VERY_LOW_QUOTA) {
          return { isPrivate: true, confidence: 'high' };
        }
      }
    }

    // All signals inconclusive — localStorage works (Signal 1 passed),
    // persisted() said no or unavailable, OPFS unavailable, quota above threshold.
    // Fail OPEN: allow registration. The localStorage write test confirmed
    // storage is functional for this session.
    return { isPrivate: false, confidence: 'low' };
  }

  /**
   * Tests whether localStorage is available and functional by performing
   * a write/read/delete cycle with a canary value.
   *
   * @returns {boolean} true if localStorage write/read succeeds, false otherwise
   * @private
   */
  static _testLocalStorageWrite() {
    const testKey = '__puffin_private_browsing_test__';
    const testValue = 'pbtest_' + Date.now();

    try {
      if (typeof localStorage === 'undefined') {
        return false;
      }

      localStorage.setItem(testKey, testValue);
      const readBack = localStorage.getItem(testKey);
      localStorage.removeItem(testKey);

      return readBack === testValue;
    } catch (error) {
      // Narrowed (SWALLOWED-CATCH U3): the browser refuses a blocked or full store with a
      // DOMException (SecurityError / QuotaExceededError) — that IS the answer "cannot write".
      // Anything else is a defect and surfaces.
      if (!(error instanceof DOMException)) throw error;
      return false;
    }
  }

  /**
   * Detects if storage is actually available (not just exists)
   * Tests with actual write/read/delete cycle to detect Tracking Prevention blocks
   *
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   * @returns {boolean} True if storage is working, false if blocked
   */
  isStorageAvailable(type = STORAGE_TYPE.LOCAL) {
    const testKey = '__storage_availability_test__';
    const testValue = 'test_' + Date.now();
    const storage = this._getStorage(type);

    if (!storage) {
      return false;
    }

    try {
      storage.setItem(testKey, testValue);
      const result = storage.getItem(testKey);
      storage.removeItem(testKey);
      return result === testValue;
    } catch (error) {
      // Narrowed (SWALLOWED-CATCH U3): a DOMException (SecurityError / QuotaExceededError) is the
      // browser's refusal — the answer "not available". Anything else is a defect and surfaces.
      if (!(error instanceof DOMException)) throw error;
      return false;
    }
  }

  /**
   * Clears all data from storage
   *
   * @param {string} [type='local'] - Storage type ('local' or 'session')
   */
  clear(type = STORAGE_TYPE.LOCAL) {
    const cache = this._getCache(type);
    const storage = this._getStorage(type);

    cache.clear();

    if (storage) {
      try {
        storage.clear();
      } catch (error) {
        // Narrowed (SWALLOWED-CATCH U3): a blocked store refuses with a DOMException; the cache is
        // already cleared and a blocked store holds nothing this tab can reach. Anything else is a
        // defect and surfaces.
        if (!(error instanceof DOMException)) throw error;
      }
    }
  }
}
